import {formatCell} from '../csv.ts';

export class TimeSeries<T> {
  static deserialize<T>(
    s: string,
    tDeserializer: (s: string) => T,
  ): TimeSeries<T> {
    const j = JSON.parse(s) as {name: string; data: string[]};

    return new TimeSeries<T>(
      j.name,
      j.data.map(
        ps => {
          const j = JSON.parse(ps) as {year: number; value: string};

          return {
            year: j.year,
            value: tDeserializer(j.value),
          };
        },
      ),
    );
  }

  readonly _name: string;
  readonly _data: Array<{year: number; value: T}>;

  constructor(
    name: string,
    data: Array<{year: number; value: T}>,
  ) {
    this._name = name;
    this._data = data;
  }

  get name(): string {
    return this._name;
  }

  get data(): Array<{year: number; value: T}> {
    return this._data;
  }

  serialize(tSerializer: (t: T) => string): string {
    return JSON.stringify({
      name: this._name,
      data: this._data.map(
        p => JSON.stringify({
          year: p.year,
          value: tSerializer(p.value),
        }),
      ),
    });
  }
}

export class ColoredSeries<T> extends TimeSeries<T> {
  static deserialize<T>(
    s: string,
    tDeserializer: (s: string) => T,
  ): TimeSeries<T> {
    const j = JSON.parse(s) as {name: string; data: string[]; color: string};

    return new ColoredSeries<T>(
      j.name,
      j.data.map(
        ps => {
          const j = JSON.parse(ps) as {year: number; value: string};

          return {
            year: j.year,
            value: tDeserializer(j.value),
          };
        },
      ),
      j.color,
    );
  }

  _color: string;

  constructor(
    name: string,
    data: Array<{year: number; value: T}>,
    color: string,
  ) {
    super(name, data);
    this._color = color;
  }

  get color(): string {
    return this._color;
  }

  serialize(tSerializer: (t: T) => string): string {
    return JSON.stringify({
      name: this._name,
      data: this._data.map(
        p => JSON.stringify({
          year: p.year,
          value: tSerializer(p.value),
        }),
      ),
      color: this._color,
    });
  }
}

export class TimeSeriesS<T> {
  static deserialize<T>(
    s: string,
    tDeserializer: (s: string) => T,
  ): TimeSeriesS<T> {
    const j = JSON.parse(s) as string[];
    const data = j.map(tss => TimeSeries.deserialize<T>(tss, tDeserializer));
    return new TimeSeriesS<T>(data);
  }

  readonly _data: Array<TimeSeries<T>>;

  constructor(data: Array<TimeSeries<T>>) {
    this._data = data;
  }

  get data(): Array<TimeSeries<T>> {
    return this._data;
  }

  serialize(tSerializer: (t: T) => string): string {
    return JSON.stringify(
      this._data.map(series => series.serialize(tSerializer)),
    );
  }
}

// Convert AccountHistory to a CSV string.
// First column is year, ordered in numerical ascending order.
// Subsequent columns are discovered from AssetTypes in the history,
// ordered alphabetically, resulting in a grid of year vs AssetType.
export function toCsv(h: Array<TimeSeries<number>>): string {
  const by_name_year: Map<string, Map<number, number>> = new Map<string, Map<number, number>>();

  for (const ts of h) {
    const by_year = new Map<number, number>();
    by_name_year.set(ts.name, by_year);
    for (const pt of ts.data) {
      by_year.set(pt.year, pt.value);
    }
  }

  const names: string[] = [...by_name_year.keys()].sort();

  /* eslint @typescript-eslint/require-array-sort-compare: "off" */
  const years: number[] = [
    ...new Set(
      h.flatMap(
        ts => ts.data.map(
          p => p.year,
        ),
      ),
    ),
  ].sort();

  const headerCells = ['YEAR', ...names];
  const cells: string[][] = [headerCells];

  for (const y of years) {
    const rowCells = [y.toString()];
    for (const t of names) {
      const amount: number = by_name_year?.get(t)?.get(y) ?? 0;
      rowCells.push(amount.toString());
    }

    cells.push(rowCells);
  }

  const formattedCells = cells.map(rowCells => rowCells.map(cell => formatCell(cell)));
  const rows: string[] = formattedCells.map(rowCells => rowCells.join(','));
  const result: string = rows.join('\n');
  return result;
}
